// components/Footer.tsx
"use client";

import Image from "next/image";
import { useState } from "react";
import { ChevronDown, ChevronUp, PhoneCall } from "lucide-react";
import linkedinIcon from "@/../public/assets/images/icons8-linkedin.svg";
import Link from "next/link";
import ftrLogo from "@/../public/assets/images/ftr-Logo.svg";
import { Service } from "@/app/shared/type";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPhone, faEnvelope } from "@fortawesome/free-solid-svg-icons";

interface MobileFooterProps {
  services: Service[];
}

const Footer = ({ services }: MobileFooterProps) => {
  const [openSection, setOpenSection] = useState<string | null>(null);

  const toggleSection = (section: string) => {
    setOpenSection(openSection === section ? null : section);
  };

  // Generate dynamic services links
  const generateServicesLinks = () => {
    if (services.length === 0) {
      return [{ label: "Loading...", href: "#" }];
    }

    const serviceLinks = services.slice(0, 5).map((service) => ({
      label: service.name,
      href: `/services#${service.slug}`,
    }));

    // Add "More Services" link if there are more than 5 services
    if (services.length > 5) {
      serviceLinks.push({
        label: "+ More Services",
        href: "/services",
      });
    }

    return serviceLinks;
  };

  // Different menu items for each section
  const footerLinks: Record<string, { label: string | React.ReactNode; href: string }[]> = {
    Services: generateServicesLinks(),
    Quicklinks: [
      { label: "About Us", href: "/about/" },
      { label: "Blogs", href: "/blogs/" },
    ],
    // Careers: [
    //   { label: "Current Openings", href: "/careers/jobs" },
    //   { label: "Share Your Resume", href: "/careers/internships" },
    //   { label: "Our Benefits", href: "/careers/life" },
    // ],
    "Contact Us": [
      { 
        label: (
          <span className="font-poppins flex items-center gap-2">
            <PhoneCall size={16} className="text-blue-500" />
            Call Us
          </span>
        ), 
        href: "tel:+919646237320" 
      },
      { 
         label: (
           <span className="font-poppins flex items-center gap-2">
             <FontAwesomeIcon icon={faEnvelope} className="text-blue-500" size="sm" />
             Email Us at
           </span>
         ),
        href: "mailto:contactus@nimbusnode.co" 
      },
      // { label: "CTO's email", href: "mailto:ankit@nimbusnode.co" },
      // { label: "CHRO, India", href: "mailto:hr@nimbusnode.co" },
    ],
  };

  return (
    <footer className="bg-black text-white px-6 py-10">
      {/* Logo */}
      <div className="text-center mb-6">
        <div className="flex justify-center mb-2">
          <Image src={ftrLogo} alt="Nimbus Node Logo" width={200} height={100} />
        </div>
      </div>

      {/* Social Links */}
      <div className="flex justify-center gap-6 mb-6 text-sm">
      
      </div>

      {/* Call Us */}
      <div className="mb-8">
        <p className="mb-3 font-poppins">Let’s Work Together</p>
        <div className="linkedin-btn">
          <Link target="blank" href="https://www.linkedin.com/company/nimbus-node">LinkedIn
            <Image src={linkedinIcon} alt="LinkedIn" width={30} height={30} />
          </Link>
        </div>
      </div>

      {/* Dropdown Sections */}
      <div className="space-y-4 font-poppins">
        {Object.keys(footerLinks).map((section) => (
          <div key={section}>
            <button
              className="w-full flex justify-between items-center py-2 border-b border-gray-700"
              onClick={() => toggleSection(section)}
            >
              <span className="font-semibold">{section}</span>
              {openSection === section ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>

            {openSection === section && (
              <div className="pl-2 mt-2 space-y-2 text-sm text-gray-300">
                {footerLinks[section].map((item, idx) => (
                  <Link 
                    key={idx} 
                    href={item.href} 
                    className={`block hover:text-white ${
                      typeof item.label === 'string' && item.label === "+ More Services" 
                        ? "text-cyan-400 hover:text-cyan-300 font-medium" 
                        : ""
                    }`}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Footer Bottom */}
      <div className="mt-10 flex flex-col sm:flex-row justify-between text-xs text-gray-400 font-poppins">
        <p>© 2025 Nimbus Node</p>
        <Link href="#">Privacy Policy</Link>
      </div>
    </footer>
  );
};

export default Footer;
